import { 
  collection, 
  doc, 
  addDoc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { handleFirebaseAuthError } from '@/utils/firebaseErrorHandler';

export interface Organization {
  id?: string;
  name: string;
  description?: string;
  industryType?: string; // Industry type of the organization (optional for backward compatibility)
  ownerId: string; // User ID of the organization owner
  createdAt?: any;
  updatedAt?: any;
  /** User-added task category labels (merged with industry defaults in task creation UI). */
  customTaskCategories?: string[];
  settings?: {
    maxUsers?: number;
    features?: string[];
  };
  sso?: {
    enabled: boolean;
    domain?: string; // e.g., "snnmanagement.com" (required when enabled)
    provider?: 'azure-ad' | 'google' | 'okta' | 'custom'; // SSO provider type (required when enabled)
    identityPlatformTenantId?: string; // Google Cloud Identity Platform tenant ID (e.g., "snn-management-mq56m") (required when enabled)
    oidcProviderId?: string; // OIDC provider ID configured in Identity Platform tenant (e.g., "azure-ad-snn-management") (required when enabled)
    // Note: OIDC provider must be configured in Google Cloud Identity Platform tenant with:
    // - Issuer URL (e.g., https://login.microsoftonline.com/{TENANT_ID}/v2.0 for Azure AD)
    // - Client ID and Secret from the IdP
    // - Scopes: openid, email, profile
  };
}

// Default industry type for backward compatibility
export const DEFAULT_INDUSTRY_TYPE = "Accommodation and Food Services";

// Helper function to get industry type with backward compatibility
export function getIndustryType(organization: Organization | null | undefined): string {
  return organization?.industryType || DEFAULT_INDUSTRY_TYPE;
}

// Organization service
export const organizationService = {
  // Create a new organization
  createOrganization: async (organizationData: Omit<Organization, 'id'>) => {
    try {
      const organizationsRef = collection(db, 'organizations');
      
      // Trim whitespace from organization name before saving
      const trimmedName = organizationData.name.trim();
      
      // Filter out undefined values - Firestore doesn't allow undefined
      const cleanData: any = {
        name: trimmedName,
        industryType: organizationData.industryType || DEFAULT_INDUSTRY_TYPE,
        ownerId: organizationData.ownerId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // Only include optional fields if they have values
      if (organizationData.description !== undefined && organizationData.description !== null && organizationData.description !== '') {
        cleanData.description = organizationData.description;
      }
      
      if (organizationData.settings !== undefined && organizationData.settings !== null) {
        cleanData.settings = organizationData.settings;
      }
      
      const docRef = await addDoc(organizationsRef, cleanData);
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error creating organization:', error);
      return { success: false, error: error.message };
    }
  },

  // Get organization by ID
  getOrganization: async (organizationId: string) => {
    try {
      const orgRef = doc(db, 'organizations', organizationId);
      const docSnap = await getDoc(orgRef);
      
      if (docSnap.exists()) {
        const orgData = { id: docSnap.id, ...docSnap.data() } as Organization;
        // Backward compatibility: default missing industryType
        if (!orgData.industryType) {
          orgData.industryType = DEFAULT_INDUSTRY_TYPE;
          // Update in database (async, don't block)
          updateDoc(orgRef, { industryType: DEFAULT_INDUSTRY_TYPE }).catch(error => {
            console.error(`Error updating industryType for organization ${organizationId}:`, error);
          });
        }
        return { success: true, data: orgData };
      } else {
        return { success: false, error: 'Organization not found' };
      }
    } catch (error: any) {
      console.error('Error getting organization:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all organizations (for super admin)
  getAllOrganizations: async () => {
    try {
      const organizationsRef = collection(db, 'organizations');
      const q = query(organizationsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const organizations: Organization[] = [];
      const organizationsToUpdate: Array<{ id: string }> = [];
      
      querySnapshot.forEach((doc) => {
        const orgData = { id: doc.id, ...doc.data() } as Organization;
        // Backward compatibility: default missing industryType
        if (!orgData.industryType) {
          orgData.industryType = DEFAULT_INDUSTRY_TYPE;
          organizationsToUpdate.push({ id: doc.id });
        }
        organizations.push(orgData);
      });
      
      // Update organizations in database that are missing industryType (async, don't block)
      if (organizationsToUpdate.length > 0) {
        organizationsToUpdate.forEach(async ({ id }) => {
          try {
            const orgRef = doc(db, 'organizations', id);
            await updateDoc(orgRef, { industryType: DEFAULT_INDUSTRY_TYPE });
          } catch (error) {
            console.error(`Error updating industryType for organization ${id}:`, error);
          }
        });
      }
      
      return { success: true, data: organizations };
    } catch (error: any) {
      console.error('Error getting all organizations:', error);
      return { success: false, error: error.message };
    }
  },

  // Get organizations owned by a user
  getOrganizationsByOwner: async (ownerId: string) => {
    try {
      const organizationsRef = collection(db, 'organizations');
      // Filter by ownerId without orderBy to avoid index requirement
      const q = query(
        organizationsRef,
        where('ownerId', '==', ownerId)
      );
      const querySnapshot = await getDocs(q);
      
      const organizations: Organization[] = [];
      const organizationsToUpdate: Array<{ id: string }> = [];
      
      querySnapshot.forEach((doc) => {
        const orgData = { id: doc.id, ...doc.data() } as Organization;
        // Backward compatibility: default missing industryType
        if (!orgData.industryType) {
          orgData.industryType = DEFAULT_INDUSTRY_TYPE;
          organizationsToUpdate.push({ id: doc.id });
        }
        organizations.push(orgData);
      });
      
      // Update organizations in database that are missing industryType (async, don't block)
      if (organizationsToUpdate.length > 0) {
        organizationsToUpdate.forEach(async ({ id }) => {
          try {
            const orgRef = doc(db, 'organizations', id);
            await updateDoc(orgRef, { industryType: DEFAULT_INDUSTRY_TYPE });
          } catch (error) {
            console.error(`Error updating industryType for organization ${id}:`, error);
          }
        });
      }
      
      // Sort in memory by createdAt descending
      organizations.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 
                      (a.createdAt instanceof Date ? a.createdAt.getTime() : 0);
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 
                      (b.createdAt instanceof Date ? b.createdAt.getTime() : 0);
        return bTime - aTime; // Descending order
      });
      
      return { success: true, data: organizations };
    } catch (error: any) {
      console.error('Error getting organizations by owner:', error);
      // Handle auth errors (token expiration, etc.)
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message };
    }
  },

  // Get organizations by IDs (for loading all organizations a user belongs to)
  getOrganizationsByIds: async (organizationIds: string[]) => {
    try {
      console.log('🔍 getOrganizationsByIds called with:', organizationIds.length, 'organization IDs');
      if (!organizationIds || organizationIds.length === 0) {
        return { success: true, data: [] };
      }

      const organizations: Organization[] = [];
      const organizationsToUpdate: Array<{ id: string }> = [];
      const notFound: string[] = [];
      
      // Fetch each organization by ID
      // Note: Firestore doesn't support 'in' queries with more than 10 items efficiently
      // So we'll fetch them individually or in batches
      const batchSize = 10;
      for (let i = 0; i < organizationIds.length; i += batchSize) {
        const batch = organizationIds.slice(i, i + batchSize);
        console.log(`📦 Fetching batch ${i / batchSize + 1} with ${batch.length} organizations:`, batch);
        const promises = batch.map(async (orgId) => {
          try {
            const orgRef = doc(db, 'organizations', orgId);
            const orgSnap = await getDoc(orgRef);
            if (orgSnap.exists()) {
              const orgData = { id: orgSnap.id, ...orgSnap.data() } as Organization;
              console.log(`✅ Found organization: ${orgData.name} (${orgId})`);
              // Backward compatibility: default missing industryType
              if (!orgData.industryType) {
                orgData.industryType = DEFAULT_INDUSTRY_TYPE;
                organizationsToUpdate.push({ id: orgSnap.id });
              }
              organizations.push(orgData);
            } else {
              console.warn(`⚠️ Organization not found: ${orgId}`);
              notFound.push(orgId);
            }
          } catch (error) {
            console.error(`❌ Error fetching organization ${orgId}:`, error);
            notFound.push(orgId);
          }
        });
        await Promise.all(promises);
      }
      
      if (notFound.length > 0) {
        console.warn(`⚠️ ${notFound.length} organization(s) not found:`, notFound);
      }
      
      console.log(`✅ getOrganizationsByIds: Found ${organizations.length} out of ${organizationIds.length} organizations`);
      
      // Update organizations in database that are missing industryType (async, don't block)
      if (organizationsToUpdate.length > 0) {
        organizationsToUpdate.forEach(async ({ id }) => {
          try {
            const orgRef = doc(db, 'organizations', id);
            await updateDoc(orgRef, { industryType: DEFAULT_INDUSTRY_TYPE });
          } catch (error) {
            console.error(`Error updating industryType for organization ${id}:`, error);
          }
        });
      }
      
      // Sort in memory by createdAt descending
      organizations.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 
                      (a.createdAt instanceof Date ? a.createdAt.getTime() : 0);
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 
                      (b.createdAt instanceof Date ? b.createdAt.getTime() : 0);
        return bTime - aTime; // Descending order
      });
      
      return { success: true, data: organizations };
    } catch (error: any) {
      console.error('Error getting organizations by IDs:', error);
      // Handle auth errors (token expiration, etc.)
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message };
    }
  },

  // Update organization
  updateOrganization: async (organizationId: string, updates: Partial<Organization>) => {
    try {
      const orgRef = doc(db, 'organizations', organizationId);
      
      // Filter out undefined values - Firestore doesn't allow undefined
      const cleanUpdates: any = {
        updatedAt: serverTimestamp()
      };
      
      // Only include fields that are not undefined
      Object.keys(updates).forEach(key => {
        const value = (updates as any)[key];
        if (value !== undefined) {
          // Trim whitespace from organization name if being updated
          if (key === 'name' && typeof value === 'string') {
            cleanUpdates[key] = value.trim();
          } else {
            cleanUpdates[key] = value;
          }
        }
      });
      
      await updateDoc(orgRef, cleanUpdates);
      return { success: true };
    } catch (error: any) {
      console.error('Error updating organization:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete organization (soft delete or hard delete)
  deleteOrganization: async (organizationId: string) => {
    try {
      const orgRef = doc(db, 'organizations', organizationId);
      await deleteDoc(orgRef);
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting organization:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Add a custom task category for an organization.
   * Dedupes case-insensitively; trims label; enforces max 50 custom categories and 50 chars per label.
   */
  addCustomTaskCategory: async (
    organizationId: string,
    label: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const trimmed = (typeof label === 'string' ? label : '').trim();
      if (!trimmed) {
        return { success: false, error: 'Category name is required' };
      }
      if (trimmed.length > 50) {
        return { success: false, error: 'Category name must be 50 characters or less' };
      }
      const orgRef = doc(db, 'organizations', organizationId);
      const orgSnap = await getDoc(orgRef);
      if (!orgSnap.exists()) {
        return { success: false, error: 'Organization not found' };
      }
      const data = orgSnap.data();
      const existing: string[] = Array.isArray(data?.customTaskCategories) ? [...data.customTaskCategories] : [];
      const key = trimmed.toLowerCase();
      if (existing.some((c) => (c || '').trim().toLowerCase() === key)) {
        return { success: true }; // already present, idempotent
      }
      const maxCustom = 50;
      if (existing.length >= maxCustom) {
        return { success: false, error: `Maximum ${maxCustom} custom categories allowed` };
      }
      await updateDoc(orgRef, {
        customTaskCategories: [...existing, trimmed],
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error: any) {
      console.error('Error adding custom task category:', error);
      return { success: false, error: error.message };
    }
  },
};
